import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { authConfig } from '../configs/auth.config';
import { SKIP_AUTH_HEADER } from './http-context';

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const oauthService = inject(OAuthService);

  if (request.url === authConfig.tokenEndpoint || request.context.get(SKIP_AUTH_HEADER)) {
    return next(request);
  }

  const token = oauthService.getAccessToken();
  if (!token) {
    return next(request);
  }

  const addAuthorizationHeader = (req: HttpRequest<unknown>, bearerToken: string): HttpRequest<unknown> =>
    req.clone({ setHeaders: { Authorization: `Bearer ${bearerToken}` } });

  const handle401Error = (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> => {
    // No loadDiscoveryDocumentAndLogin() here: it redirects the page and races
    // both the refresh below and the /login fallback.
    return from(oauthService.refreshToken()).pipe(
      switchMap(() => {
        const newToken = oauthService.getAccessToken();
        const authReq = addAuthorizationHeader(req, newToken);
        return next(authReq);
      }),
      catchError(() => {
        localStorage.clear();
        globalThis.location.href = '/login';
        return throwError(() => new Error('Session expired. Please log in again.'));
      }),
    );
  };

  const authReq = addAuthorizationHeader(request, token);

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handle401Error(request);
      }
      return throwError(() => error);
    }),
  );
};
